import Cookies from "js-cookie";
import { COOKIE_EXPIRY_DAYS, COOKIE_KEYS } from "@/lib/constants";

// Empty in dev so the Vite proxy handles the rewrite. Set
// ``VITE_API_URL`` at build time for staging / production.
//
// Exported so endpoints that don't route through ``ServiceParams.url``
// — playground / skill / catalog hooks that build raw ``fetch`` calls
// — can prefix the gateway origin in production. Without this prefix
// the bare ``/v1/...`` path resolves to ``console.monago.io``
// (the static-asset origin) and nginx replies with 405.
export const API_URL: string = import.meta.env.VITE_API_URL ?? "";

type TokenType = "Bearer";

export const COOKIE_OPTIONS = {
  secure: import.meta.env.PROD,
  sameSite: "strict" as const,
  expires: COOKIE_EXPIRY_DAYS,
} as const;

export function getAuthorizationHeader(
  tokenType: TokenType,
  token?: string
): string {
  const value = token ?? Cookies.get(COOKIE_KEYS.ACCESS_TOKEN) ?? "";
  return `${tokenType} ${value}`;
}

export function configureHeaders(opts: {
  contentType?: string;
  tokenType?: TokenType;
  token?: string;
  isFileUpload?: boolean;
} = {}): Headers {
  const headers = new Headers();
  if (opts.contentType) {
    headers.append("Content-Type", opts.contentType);
  } else if (!opts.isFileUpload) {
    headers.append("Content-Type", "application/json");
  }
  const token = opts.token ?? Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
  if (token) {
    headers.append("Authorization", `${opts.tokenType ?? "Bearer"} ${token}`);
  }
  return headers;
}

export interface ServiceParams {
  url: string;
  config: typeof configureHeaders;
}

export const baseParams: ServiceParams = {
  url: API_URL,
  config: configureHeaders,
};

// ============================================================================
// Errors
// ============================================================================

/**
 * Carries the upstream HTTP status + a parsed error envelope so
 * callers can branch on shape without re-parsing the body. The auto
 * 401 → refresh path in :func:`useApi` keys off ``status``.
 */
export class ApiError extends Error {
  status: number;
  code?: string;
  payload?: unknown;

  constructor(
    message: string,
    options: { status: number; code?: string; payload?: unknown }
  ) {
    super(message);
    this.name = "ApiError";
    this.status = options.status;
    this.code = options.code;
    this.payload = options.payload;
  }
}

/**
 * Read ``fetch`` response → ApiError on non-2xx, JSON otherwise.
 * Tolerates empty responses (204 etc.) by returning ``undefined``.
 */
export async function unwrapJson<T>(response: Response): Promise<T> {
  if (response.ok) {
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }
  let payload: unknown = undefined;
  let message = `HTTP ${response.status}`;
  let code: string | undefined;
  try {
    const body = await response.json();
    payload = body;
    if (typeof body === "object" && body !== null) {
      const env = body as { error?: string; message?: string };
      message = env.message ?? message;
      code = env.error;
    }
  } catch {
    // Non-JSON error body; fall through with the default message.
  }
  throw new ApiError(message, { status: response.status, code, payload });
}

// ============================================================================
// Token refresh — used by useApi on 401
// ============================================================================

let inflightRefresh: Promise<boolean> | null = null;

export async function refreshAccessToken(): Promise<boolean> {
  // Coalesce concurrent refresh attempts so we don't burn the
  // refresh token. The lock is process-local — a single tab can
  // call this from many hooks simultaneously and only the first
  // one fires the network call.
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = (async () => {
    const refreshToken = Cookies.get(COOKIE_KEYS.REFRESH_TOKEN);
    if (!refreshToken) return false;
    try {
      const response = await fetch(`${API_URL}/v1/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!response.ok) return false;
      const data = (await response.json()) as {
        access_token: string;
        refresh_token: string;
      };
      Cookies.set(COOKIE_KEYS.ACCESS_TOKEN, data.access_token, COOKIE_OPTIONS);
      Cookies.set(
        COOKIE_KEYS.REFRESH_TOKEN,
        data.refresh_token,
        COOKIE_OPTIONS
      );
      return true;
    } catch {
      return false;
    } finally {
      inflightRefresh = null;
    }
  })();
  return inflightRefresh;
}
